import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Eye, Link2, Unlink, Upload, X } from 'lucide-react';

import {
  fetchClienteArtefatos,
} from '@/api/clientes';
import { downloadArtefato, fetchArtefatos, previewArtefato, updateArtefato, type Artefato } from '@/api/client';
import { DocumentPreviewDialog } from '@/components/DocumentPreviewDialog';
import { UploadDocumentoDialog } from '@/components/UploadDocumentoDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileTypeIcon } from '@/components/ui/FileTypeIcon';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { formatDate, formatFileSize } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';

function isPreviewable(mime: string): boolean {
  return mime === 'application/pdf' || mime.startsWith('image/');
}

export function ClienteDocumentosTab({
  clienteId,
  clienteNome,
  somenteLeitura = false,
}: {
  clienteId: string;
  clienteNome: string;
  /** Cliente de colega (visão do escritório): ver e baixar, sem vincular nem enviar. */
  somenteLeitura?: boolean;
}) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [showLinkSearch, setShowLinkSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // Preview state
  const [previewDoc, setPreviewDoc] = useState<Artefato | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  const { data, isLoading, error } = useQuery<Artefato[]>({
    queryKey: queryKeys.clientes.artefatos(clienteId),
    queryFn: () => fetchClienteArtefatos(clienteId),
  });

  const { data: searchResults } = useQuery({
    queryKey: [...queryKeys.documentos.artefatos, 'link-search', searchTerm],
    queryFn: () => fetchArtefatos({ search: searchTerm, page_size: 10 }),
    enabled: showLinkSearch && searchTerm.length >= 2,
  });
  // Documento de colega não se vincula aqui: é só para ler (a escrita no compartilhado é o Bloco 3).
  const availableArtefatos = (searchResults as { items?: Artefato[] })?.items?.filter(
    (a: Artefato) => !a.cliente_id && a.origem !== 'compartilhado'
  ) ?? [];

  const linkMutation = useMutation({
    mutationFn: (artefatoId: string) => updateArtefato(artefatoId, { cliente_id: clienteId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clientes.artefatos(clienteId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentos.artefatos });
      showSuccess('Documento vinculado ao cliente');
      setShowLinkSearch(false);
      setSearchTerm('');
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const unlinkMutation = useMutation({
    mutationFn: (artefatoId: string) => updateArtefato(artefatoId, { cliente_id: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clientes.artefatos(clienteId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.documentos.artefatos });
      showSuccess('Documento desvinculado do cliente');
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const handlePreview = useCallback(async (art: Artefato) => {
    if (!isPreviewable(art.mime)) {
      handleDownload(art);
      return;
    }
    try {
      const { download_url } = await previewArtefato(art.id);
      setPreviewUrl(download_url);
      setPreviewDoc(art);
      setPreviewDialogOpen(true);
    } catch {
      showError('Erro ao gerar visualização');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showError]);

  const handleDownload = useCallback(async (art: Artefato) => {
    try {
      const { download_url } = await downloadArtefato(art.id);
      const a = document.createElement('a');
      a.href = download_url;
      a.download = art.nome;
      a.click();
    } catch (err) {
      notifyMutationError(err, showError);
    }
  }, [showError]);

  if (isLoading) return <div className="flex justify-center py-6"><Spinner size={32} label="Carregando documentos..." /></div>;
  if (error) return <Alert variant="destructive"><AlertDescription>{classifyError(error).message}</AlertDescription></Alert>;

  return (
    <div className="space-y-3">
      {/* Ações */}
      <div className={somenteLeitura ? 'hidden' : 'flex gap-2'}>
        {!showLinkSearch && (
          <Button variant="outline" size="sm" onClick={() => setShowLinkSearch(true)}>
            <Link2 className="mr-1.5 h-3.5 w-3.5" />
            Vincular existente
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => setUploadDialogOpen(true)}>
          <Upload className="mr-1.5 h-3.5 w-3.5" />
          Enviar novo
        </Button>
      </div>

      <UploadDocumentoDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        fixedClienteId={clienteId}
        fixedClienteNome={clienteNome}
        extraInvalidateKeys={[queryKeys.clientes.artefatos(clienteId)]}
      />

      {/* Buscar para vincular */}
      {showLinkSearch && (
        <div className="space-y-2 rounded-md border border-border p-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Buscar documento para vincular</p>
            <Button variant="ghost" size="sm" onClick={() => { setShowLinkSearch(false); setSearchTerm(''); }}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <Input
            autoFocus
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Digite o nome do documento..."
          />
          {searchTerm.length >= 2 && (
            <div className="max-h-40 overflow-y-auto">
              {availableArtefatos.length === 0 ? (
                <p className="py-2 text-center text-xs text-muted-foreground">Nenhum documento sem cliente encontrado.</p>
              ) : (
                availableArtefatos.map((a: Artefato) => (
                  <div
                    key={a.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                    onClick={() => linkMutation.mutate(a.id)}
                  >
                    <FileTypeIcon mime={a.mime} size="sm" />
                    <span className="min-w-0 [overflow-wrap:anywhere]">{a.nome}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{a.tipo}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Lista de documentos */}
      {!data?.length ? (
        <p className="py-4 text-center text-sm text-muted-foreground">Nenhum documento vinculado.</p>
      ) : (
        <div className="space-y-2">
          {data.map((art) => (
            <div
              key={art.id}
              className="flex items-center justify-between rounded border p-3 transition-colors hover:bg-muted/50"
            >
              <div
                className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                onClick={() => handlePreview(art)}
              >
                <FileTypeIcon mime={art.mime} />
                <div className="min-w-0">
                  <p className="[overflow-wrap:anywhere] text-sm font-medium">{art.nome}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-muted-foreground">
                      {formatFileSize(art.tamanho)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(art.created_at)}
                    </span>
                    {art.tags?.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-[11px]">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                {isPreviewable(art.mime) && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handlePreview(art)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Visualizar</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDownload(art)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Baixar</TooltipContent>
                </Tooltip>
                {!somenteLeitura && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => unlinkMutation.mutate(art.id)}
                        disabled={unlinkMutation.isPending}
                      >
                        <Unlink className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Desvincular</TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview dialog */}
      <DocumentPreviewDialog
        open={previewDialogOpen}
        onOpenChange={setPreviewDialogOpen}
        document={previewDoc}
        previewUrl={previewUrl}
        onDownload={() => previewDoc && handleDownload(previewDoc)}
      />
    </div>
  );
}
